/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import { Locale } from '@core/Entities/Locale/Locale.entity';
import { KenticoImage } from '@shared-components/KenticoImage/KenticoImage';
import { RichText } from '@page-builder/Components/RichText/RichText';
import cn from 'classnames';
import {
  FinanceHelpModalContent,
  FinanceItem,
  FinanceProductOptions,
} from '../../../Entities/FinanceHelpModal.entity';
import { financeHelpConfig, videoUrl, guideUrl } from './FinanceHelpModal.constants';
import styles from './FinanceHelpModal.module.css';
import { Typography } from '@gds/Typography/Typography';
import { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { useAtomValue } from 'jotai';
import { eligibleProductsAtom } from '../../../Atoms/EligibleProducts.atom';
import { calculationAtom } from '../../../Atoms/Calculation.atom';
import { CAPTIVE_MAP } from '../Helpers/FinanceMap';

type FinanceModalStrings = {
  paymentPlan: string;
  dealership: string;
  pcpFilm?: string;
  pcpGuide?: string;
  hpFilm?: string;
  hpGuide?: string;
  downPayment: string;
  monthlyInstallments: string;
  closingOptions: string;
};
interface FinanceHelpModalProps {
  locale: Locale;
  financeMessages: FinanceHelpModalContent;
  financeModalStrings: FinanceModalStrings;
  vehicle: Vehicle;
}

/**
 * For a finance product to show up in the modal it needs to exist in the "Finance helper v2" page on kontent.ai
 * Make sure it exists in the correct locale and with the correct financeType
 * https://app.kontent.ai/3b3d460e-c5ae-0195-6b86-3ac7fb9d52db/content-inventory/bf30260e-3886-473f-8812-884c21aa7110/content/43e48387-b456-4474-a46a-f0a2632dfffd
 */
export const FinanceHelpModal = ({
  financeMessages,
  financeModalStrings,
  locale,
  vehicle,
}: FinanceHelpModalProps) => {
  const eligibleProducts = useAtomValue(eligibleProductsAtom)!;
  const calculation = useAtomValue(calculationAtom)!;
  const config = financeHelpConfig[locale];
  const financeMessagesForVehicle = getFinanceMessagesForVehicle({
    vehicle,
    financeMessages,
    eligibleProducts,
    captiveId: calculation.captiveId,
  });

  return (
    <>
      <div className={styles.financeModal}>
        <div className={styles.financeModalDescription}>
          <RichText noPadding richText={financeMessagesForVehicle.heading} />
        </div>
        <div className={styles.financeModalContent}>
          <FinanceOptions
            financeMessagesForVehicle={financeMessagesForVehicle}
            financeModalStrings={financeModalStrings}
            {...config}
          />
        </div>
        {config.showDealership && (
          <div className={styles.financeModalFooter}>
            <Typography variant="caption2" Component="span">
              {financeModalStrings.dealership}
            </Typography>
          </div>
        )}
      </div>
      <div className={styles.financeModalFooter}>
        <Typography variant="caption2" Component="span">
          {financeModalStrings.dealership}
        </Typography>
      </div>
    </>
  );
};

function FinanceOptions({
  financeMessagesForVehicle,
  showFinanceUsp,
  showPaymentPlanText,
  showPaymentPlanImage,
  showPcpGuide,
  showHPGuide,
  financeModalStrings,
}: {
  financeMessagesForVehicle: FinanceHelpModalContent;
  financeModalStrings: FinanceModalStrings;
  showFinanceUsp: boolean;
  showPaymentPlanText: boolean;
  showPaymentPlanImage: boolean;
  showPcpGuide: boolean;
  showHPGuide: boolean;
}) {
  const { financeProducts } = financeMessagesForVehicle;

  return financeProducts.map(financeOption => (
    <div
      className={cn(styles.financeModalOption, { [styles.wide]: financeProducts.length < 3 })}
      key={financeOption.financetype[0].codename}
    >
      <div className={styles.financeModalOptionTitle}>
        <Typography variant="h5" Component="span">
          {financeOption.subheader}
        </Typography>
      </div>
      <div className={styles.financeModalOptionSubtitle}>
        <Typography variant="body3" Component="span">
          {financeOption.description}
        </Typography>
      </div>
      {showFinanceUsp && (
        <div className={styles.financeModalUspsWrapper}>
          <UspItems uspItems={financeOption.financeitems} />
        </div>
      )}
      {(showPaymentPlanText || showPaymentPlanImage) && (
        <div className={styles.financeModalPaymentPlan}>
          {showPaymentPlanImage && (
            <>
              <Typography variant="subheading3">{financeModalStrings.paymentPlan}</Typography>

              <div className={styles.financeModalImage}>
                <ImageLabels labels={financeOption.bodyimagelabels} strings={financeModalStrings} />
                <KenticoImage src={financeOption.bodyimage[0]} alt="car" fill />
              </div>
            </>
          )}
          {showPaymentPlanText && (
            <>
              <Typography variant="subheading3">{financeOption.bodytitle}</Typography>
              <div className={styles.financeModalUspsWrapper}>
                <UspItems uspItems={financeOption.bodyitems} />
              </div>
            </>
          )}
        </div>
      )}
      {showPcpGuide && financeOption.financetype[0].codename === 'pcp' && (
        <div className={styles.financeModalLinksFooter}>
          <a
            href={videoUrl.pcp}
            className={styles.financeModalGuideLinks}
            target="_blank"
            rel="noreferrer"
          >
            <Typography variant="body2" Component="span">
              {financeModalStrings.pcpFilm}
            </Typography>
          </a>
          <a
            href={guideUrl.pcp}
            className={styles.financeModalGuideLinks}
            target="_blank"
            rel="noreferrer"
          >
            <Typography variant="body2" Component="span">
              {financeModalStrings.pcpGuide}
            </Typography>
          </a>
        </div>
      )}

      {showHPGuide && financeOption.financetype[0].codename === 'hp' && (
        <div className={styles.financeModalLinksFooter}>
          <a
            href={videoUrl.hp}
            className={styles.financeModalGuideLinks}
            target="_blank"
            rel="noreferrer"
          >
            <Typography variant="body2" Component="span">
              {financeModalStrings.hpFilm}
            </Typography>
          </a>
          <a
            href={guideUrl.hp}
            className={styles.financeModalGuideLinks}
            target="_blank"
            rel="noreferrer"
          >
            <Typography variant="body2" Component="span">
              {financeModalStrings.hpGuide}
            </Typography>
          </a>
        </div>
      )}
    </div>
  ));
}

function UspItems({ uspItems }: { uspItems: FinanceItem[] }) {
  return uspItems.map(item => (
    <li className={styles.uspItem} key={item.description}>
      {item.image.length > 0 && (
        <div className={styles.uspIcon}>
          <KenticoImage
            src={item.image[0]}
            alt={item.title}
            fill
            style={{ objectFit: 'contain' }}
            sizes="(max-width: 768px) 20vw, (max-width: 1200px) 33vw"
          />
        </div>
      )}
      <div>{item.description}</div>
    </li>
  ));
}

function ImageLabels({
  labels,
  strings,
}: {
  labels: FinanceProductOptions;
  strings: FinanceModalStrings;
}) {
  if (!labels.length) {
    return null;
  }

  return (
    <ul className={styles.imageHelpers}>
      {labels.map(option => (
        <li>{strings[option.name]}</li>
      ))}
    </ul>
  );
}

const PRODUCT_ID_MAPPING = {
  privateLeasing: {
    [CAPTIVE_MAP.PFS]: 'privateLeasingPFS',
  },
};

function getFinanceMessagesForVehicle({
  vehicle,
  financeMessages,
  eligibleProducts,
  captiveId,
}: {
  vehicle: Vehicle;
  financeMessages: FinanceHelpModalContent | null;
  eligibleProducts: string[];
  captiveId: string;
}): FinanceHelpModalContent {
  if (!vehicle.pricing.hasFinance || !financeMessages) {
    return {
      heading: '',
      financeProducts: [],
    };
  }

  const productsForCaptiveId = eligibleProducts.map(productId => {
    return (
      PRODUCT_ID_MAPPING[productId as keyof typeof PRODUCT_ID_MAPPING]?.[captiveId] ?? productId
    );
  });

  const financeProducts = financeMessages.financeProducts.filter(financeProduct => {
    const [financeType] = financeProduct.financetype;
    return productsForCaptiveId.includes(financeType.name);
  });

  return {
    heading: financeMessages.heading ?? '',
    financeProducts,
  };
}
